import { useState } from "react";
import { Box, Button, Center, Image, Paper, Stack, Text } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import EcosystemCorridorHeadDetailsModal from "@/pages/ecosystem-heads/components/EcosystemCorridorHeadDetailsModal";
import {
  ECOSYSTEM_CORRIDOR_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";

const CARD_BORDER = "#BFDBFE";
const PROMO_LOGO_HEIGHT = 38;

type EcosystemCorridorPromoCardProps = {
  fixedWidth?: number;
  fullWidth?: boolean;
  variant?: "carousel" | "explore";
};

export default function EcosystemCorridorPromoCard({
  fixedWidth,
  fullWidth = false,
  variant = "carousel",
}: EcosystemCorridorPromoCardProps) {
  const isExplore = variant === "explore";
  const cardHeight = isExplore ? ECOSYSTEM_EXPLORE_CARD_HEIGHT : ECOSYSTEM_CORRIDOR_CARD_HEIGHT;
  const cardWidth = fullWidth && isExplore ? undefined : isExplore ? ECOSYSTEM_EXPLORE_CARD_WIDTH : fixedWidth;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const cardMotionStyle = isExplore
    ? {
        boxShadow: isHovered
          ? "0 16px 40px rgba(4, 48, 92, 0.22)"
          : "0 4px 20px rgba(15, 23, 42, 0.08)",
        transition: "box-shadow 0.25s ease",
      }
    : undefined;

  return (
    <>
    <Paper
      radius="lg"
      shadow="none"
      h={cardHeight}
      onMouseEnter={isExplore ? () => setIsHovered(true) : undefined}
      onMouseLeave={isExplore ? () => setIsHovered(false) : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${CARD_BORDER}`,
        overflow: "hidden",
        background: "linear-gradient(145deg, #04305c 0%, #0b4a8c 55%, #1565c0 100%)",
        ...(cardWidth
          ? { flex: "0 0 auto", width: cardWidth, maxWidth: "85vw" }
          : fullWidth && isExplore
            ? { width: "100%" }
            : undefined),
        ...cardMotionStyle,
      }}
    >
      <Stack gap="md" p="lg" style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="sm" align="center" style={{ flex: 1 }}>
          <Center w="100%">
            <Box
              px="sm"
              py={6}
              style={{
                borderRadius: 12,
                backgroundColor: "#FFFFFF",
                border: "1px solid rgba(255, 255, 255, 0.22)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                lineHeight: 0,
              }}
            >
              <Image src="/gtLogo.svg" alt="GTsetu" h={PROMO_LOGO_HEIGHT} w="auto" />
            </Box>
          </Center>

          <Text
            fw={700}
            size="sm"
            tt="uppercase"
            ta="center"
            c="#FFFFFF"
            lh={1.35}
          >
            Be an Ecosystem Corridor Head
          </Text>
          <Text
            size="xs"
            ta="center"
            c="rgba(255, 255, 255, 0.82)"
            lh={1.45}
            lineClamp={3}
          >
            Lead a cross-border corridor, connect verified businesses, and grow trade in your sector.
          </Text>
        </Stack>

        <Button
          size="sm"
          fullWidth
          mt="auto"
          rightSection={<IconArrowRight size={14} />}
          onClick={() => setDetailsOpen(true)}
          styles={{
            root: {
              minHeight: 38,
              fontWeight: 600,
              backgroundColor: "#FFFFFF",
              color: "#04305c",
              "&:hover": { backgroundColor: "#EFF6FF" },
            },
          }}
        >
          See Details
        </Button>
      </Stack>
    </Paper>

    <EcosystemCorridorHeadDetailsModal opened={detailsOpen} onClose={() => setDetailsOpen(false)} />
    </>
  );
}
